import { useEffect } from 'react'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'

import { useAPIGetPelaksanaanConfig } from 'api/kinerja/useAPIKinerja'
import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'
import PanelInfo from 'app/KinerjaPage/pages/PelaksanaanV2/components/PanelInfo'
import CardDeadlinePeriod from 'app/KinerjaShared/components/CardDeadlinePeriod'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import { formatDate, isDateValid } from 'utils/datetime'
import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import { tahapanPenetapanPredikatKinerja } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/tahapanPenetapanPredikatKinerja'
import { getSatuanPendidikanByNpsn } from 'app/KinerjaShared/utils/profile'
import useDemoContext from 'app/KinerjaPage/hooks/useDemoContext'
import { PANDUAN_PENGELOLAAN_GURU_TENTANG_PENILAIAN_URL } from 'app/KinerjaShared/utils/panduan'

type LangkahPenetapanPredikatKinerjaProps = {
  npsn: string
}

const LangkahPenetapanPredikatKinerja = ({
  npsn,
}: LangkahPenetapanPredikatKinerjaProps) => {
  const { trackEvent } = useSKPPelaksanaanTrackEvent()
  const { profile, isLoading: isLoadingProfile } = useRoleContext()
  const { isDemoMode } = useDemoContext()

  const { data: dataPelaksanaanConfig } = useAPIGetPelaksanaanConfig({
    params: { key: 'PENETAPAN_PREDIKAT_KINERJA' },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
    },
  })

  useEffect(() => {
    trackEvent({
      activity_type: 'penilaian_ks_open_penetapan_kinerja',
      screen_name: 'ks_penetapan_kinerja',
      event_trigger: 'ks_penilaian_hub',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isLoadingProfile)
    return (
      <div className="w-full flex justify-center pt-20">
        <Spinner />
      </div>
    )

  // deadline date
  const deadlineDate =
    dataPelaksanaanConfig?.data?.predicatePerformanceDeadlineDate
  const isValidDeadlineDate = isDateValid(deadlineDate)
  const formattedDeadlineDate = isValidDeadlineDate
    ? formatDate(deadlineDate)
    : '-'

  return (
    <div className="pt-12 px-8 pb-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex justify-between items-center">
          <Header
            title="Penetapan Predikat Kinerja"
            className="mb-9"
            rightNode={
              <>
                <section className="bg-white rounded">
                  <Button
                    variant="ghost"
                    className="flex gap-1.5 !py-4"
                    as="a"
                    target="_blank"
                    type={undefined}
                    href={PANDUAN_PENGELOLAAN_GURU_TENTANG_PENILAIAN_URL}
                    onClick={() => {
                      trackEvent({
                        activity_type:
                          'penilaian_ks_pk_click_panduan_penetapan',
                        screen_name: 'ks_penetapan_kinerja',
                        event_trigger: 'ks_penilaian_hub',
                      })
                    }}
                  >
                    <Icon fontSize="sm" color="informational">
                      help
                    </Icon>
                    Panduan Penetapan
                  </Button>
                </section>
                <PanelInfo
                  isLoading={isLoadingProfile}
                  label="Satuan Pendidikan"
                  value={getSatuanPendidikanByNpsn(profile, npsn, isDemoMode)}
                />
              </>
            }
          />
        </div>
        <div className="bg-white rounded-lg border border-subdued">
          <div className="p-6">
            <div className="space-y-4">
              <Text variant="heading-md">
                Langkah-langkah Penetapan Predikat Kinerja
              </Text>
              <CardDeadlinePeriod deadlineDate={formattedDeadlineDate} />
              <div className="flex gap-2 items-center">
                {tahapanPenetapanPredikatKinerja.map((thisStep, index) => (
                  <>
                    <thisStep.CardComponent
                      {...thisStep}
                      npsn={npsn}
                      key={`step_${index}`}
                      overrideState="locked"
                    />
                    {index + 1 < tahapanPenetapanPredikatKinerja.length && (
                      <Icon>arrow_forward</Icon>
                    )}
                  </>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LangkahPenetapanPredikatKinerja
